import { IRowData } from "@multiweb/types/contracts";
import { IOrderBy, OrderType } from "../../Global/Base/DataSources/Interfaces";
import { PromiseUtils } from "../../Global/Base/Promises/PromiseUtils";
import { StringUtils } from "../../Global/Base/Utils/StringUtils";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { UIManager } from "../UI/UI/UIManager";
import { BaseTreeViewGrid } from "./BaseTreeViewGrid";
import { Column } from "./DataGridSubcontrols/Columns/Column";
import { GroupingTreeHeaderRow } from "./DataGridSubcontrols/Components/GroupingTreeHeaderRow";
import { GroupingTreeViewDataRow } from "./DataGridSubcontrols/Components/GroupingTreeViewDataRow";
import { CollapseState } from "./DataGridSubcontrols/Components/TreeViewDataRow";
import { GroupingTemplate } from "./DataGridSubcontrols/Templates/GroupingTemplate";
import { GroupingTreeViewDataSource, IAdditionalGroupingParams, IGroupingDataSourceLoadInfo } from "./DataSource/GroupingTreeViewDataSource";
import { IDataGrid } from "./IDataGrid";
import { IDataGridDataShownEventData, IGroupingDataGridSelectionChangedEventData } from "./IEvent";

export class GroupingTreeViewGrid extends BaseTreeViewGrid<GroupingTreeViewDataRow> implements IDataGrid<GroupingTreeViewDataRow> {
    //#region Fields
    private _dataSource: GroupingTreeViewDataSource;
    //#endregion Fields

    //#region Properties
    /** Seznam atributů, které jsou zobrazeny v grupovacím stromu */
    public attributesToGroup: Array<string>;
    /** Sloupce seznamu, pomocí kterých se provádí formátování dat */
    public formattingColumns: Column[];
    /** Informace o předchozím stavu stromu */
    private _previousSelection: IPreviousSelectionData[];

    /**
     * Řádek hlavičky seznamu
     */
    public override headerRow: GroupingTreeHeaderRow;

    public override get dataSource(): GroupingTreeViewDataSource {
        return this._dataSource;
    }

    public override set dataSource(v: GroupingTreeViewDataSource) {
        this._dataSource = v;
        this._dataSource.dataLoaded.add((_sender, eventData) => {
            if (!this.visible)
                return;

            this._showData(eventData);
            this._areDataShowedDefferred.resolve();
            this.callEvent("DataShown", { data: eventData.dataGenerator } as IDataGridDataShownEventData);
        });

        this.dataSource.loadingStarted.add(() => UIManager.instance.showLoader());
        this.dataSource.loadingEnded.add(() => UIManager.instance.hideLoader());
    }

    public override get orderBy(): IOrderBy[] {
        return this.dataSource ? this.dataSource.orderBy : [];
    }

    public override get columns(): Column[] {
        return this._columns;
    }
    //#endregion Properties

    constructor(self?: any) {
        super(self);
    }

    protected override _constructor(): void {
        super._constructor();

        this.classList.add("grouping");

        const listDiv = createElement({ classes: "p-list-div" });

        this.headerRow = new GroupingTreeHeaderRow();

        this.appendChild(this.headerRow);
        this.appendChild(listDiv);
        listDiv.appendChild(this._dataTable);

        this.addEventListener("childDataRequest", (e: CustomEvent) => {
            if (e.target instanceof GroupingTreeViewDataRow) {
                this._areDataShowedDefferred = PromiseUtils.deferStateful<void>();
                this._loadChildData(e.target).then(() => e.detail.promiseResolvedFnc());
            }
        });

        this._columns = [new Column()]; // Je potřeba jen jeden sloupec, ve kterém budou všechna data
        this._columns[0].itemTemplate = new GroupingTemplate(); // Stará se o konverze dat do správného formátu
        this._dataRowFactory = () => new GroupingTreeViewDataRow();
    }

    //#region Methods
    /**
     * Metoda pro odstranění všech řádků datagridu
     */
    public override clearDataRows(): void {
        this.dataRows.forEach(row => row.remove());
        this.dataRows = [];
        this.skippedRowsCount = 0;
    }

    /** Načte znovu všechna data podle aktuálního seznamu požadovaných atributů
     * @param keepSelectedRecord Zda má být označen záznam vybraný před refreshem včetně otevření větví stromu k tomuto záznamu
     */
    public override async refreshData(keepSelectedRecord?: boolean): Promise<void> {
        const scrollPosition = this._dataTable.scrollTop;

        this.clearDataRows();

        await this.loadNewData();
        await this.dataShowedPromise;

        let openedRow: GroupingTreeViewDataRow;

        if (keepSelectedRecord && this._previousSelection) {
            for (const item of this._previousSelection) {
                openedRow = this.dataRows.find(row => row.dataValue === item.rowValue);

                if (openedRow && !openedRow.isLastNode && item.expanded) {
                    openedRow.state = CollapseState.expanded;

                    await this.dataShowedPromise;
                }
            }

            if (openedRow) {
                openedRow.isSelected = true;
            } else {
                this._triggerSelectionChanged("-1"); // Není co vybrat, je tedy potřeba načíst všechna data do seznamu bez omezení
            }

            // Scroll je nastaven až na závěr, kdy jsou načteny všechny požadované větve.
            this._dataTable.scrollTop = scrollPosition;
        }
    }

    /** Nastaví první atribut z pole do dataSource a spustí načítání */
    public async loadNewData(): Promise<void> {
        this._areDataShowedDefferred = PromiseUtils.deferStateful<void>();
        this.dataSource.setAttrs([this.attributesToGroup[0]]);
        this.dataSource.orderBy = [{ attributeName: this.attributesToGroup[0], order: OrderType.ascending }];

        await this.dataSource.loadNewData();
    }

    /**
     * Spustí načtení dat do podřízené větve stromu
     * @param parent Prvek, pro který mají být načteny podřízené záznamy
     */
    protected override async _loadChildData(parent: GroupingTreeViewDataRow): Promise<void> {
        const data = this._prepareConditionData(parent);

        await this.dataSource.loadChildData(this.attributesToGroup[data.length], parent.dataKey, data);
    }

    /**
     * Vytvoří strukturu s daty pro vygenerování podmínek
     * @param row Řádek stromu, od kterého se začíná s generováním podmínky
     * @param fillPreviousSelection Zda má být aktualizována historie stromu pro případný refresh
     */
    protected _prepareConditionData(row: GroupingTreeViewDataRow, fillPreviousSelection?: boolean): Array<IAdditionalGroupingParams> {
        const data: Array<IAdditionalGroupingParams> = new Array<IAdditionalGroupingParams>({ condition: row.dataAttribute, param: row.dataValue?.valueOf() });

        if (fillPreviousSelection) {
            this._previousSelection = new Array<IPreviousSelectionData>({ rowValue: row.dataValue, expanded: (row.state === CollapseState.expanded) });
        }

        while (row.parentRow) {
            row = row.parentRow;

            data.push({ condition: row.dataAttribute, param: row.dataValue?.valueOf() });

            if (fillPreviousSelection) {
                this._previousSelection.push({ rowValue: row.dataValue, expanded: (row.state === CollapseState.expanded) });
            }
        }

        if (fillPreviousSelection) {
            this._previousSelection = this._previousSelection.reverse();
        }

        return data;
    }

    /**
     * Vytvoří nové řádky s daty
     * @param eventData Informace o načtených datech
     */
    protected override _showData(eventData: IGroupingDataSourceLoadInfo): void {
        if (eventData.rowsCount === 0) {
            return;
        }

        const attributeIndex = this.attributesToGroup.indexOf(eventData.attribute);
        const hasChildren = (attributeIndex > -1 && attributeIndex < this.attributesToGroup.length - 1); // Potřebujeme zjistit, zda se jedná o poslední atribut v poli -> pak již nemá potomky
        const data = eventData.dataGenerator;
        const parent = this.dataRows.find(item => item.dataKey === eventData.parentId);

        // Najde a nastaví sloupec z provázaného seznamu, který bude provádět formátování dat do stromu
        (this.columns[0].itemTemplate as GroupingTemplate).formattingColumn = this.formattingColumns.find((column) => StringUtils.compareCaseInsensitive(column.attrName, eventData.attribute));

        for (const irow of data()) {
            irow.hasChildren = hasChildren;
            irow.attribute = eventData.attribute;

            this._appendRow(parent, irow);
        }
    }

    /**
     * Vloží nový řádek na patřičné místo ve stromu
     * @param parent Rodičovský řádek, do kterého má být nový záznam vložen
     * @param rowData Data ke zobrazení
     */
    private _appendRow(parent: GroupingTreeViewDataRow, rowData: IRowData): void {
        const newRow = this._createRow(rowData);

        newRow.render();

        if (parent) {
            parent.appendChild(newRow);
        } else {
            this._dataTable.appendChild(newRow);
        }
    }

    /**
     * Vyolává událost určující, že došlo k výběru záznamu
     * @param id ID záznamu
     * @param data Data záznamu
     */
    protected override _triggerSelectionChanged(id: string, data?: IRowData): void {
        const conditionData = (id != "-1" ? this._prepareConditionData(this.dataRows.find(item => item.dataKey === id), true) : null);

        this.callEvent("SelectionChanged", { dataGrid: this, rowID: id, data: data, conditionData: conditionData } as IGroupingDataGridSelectionChangedEventData, true, true);
    }

    public override selectAllRows(): void {
        throw new Error("Method not implemented.");
    }

    public override dispose(): void {
        super.dispose();
    }

    //#endregion Methods
}

/** Informace o předchozím stavu gridu  */
interface IPreviousSelectionData {
    rowValue: any;
    expanded: boolean;
}

registerCustomElement("tsc-GroupingTreeViewGrid", GroupingTreeViewGrid);
